import {
  useState,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  type ReactNode,
} from 'react';
import {
  createRootRouteWithContext,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from '@tanstack/react-router';
import { SignInScreen } from '@/features/auth/SignInScreen';
import { BootFrame } from '@/features/auth/BootFrame';
import { storedClaimToken } from '@/lib/portal-claim';
import { resolveRootStage } from '@/features/auth/root-stage';
import { RoleErrorScreen } from '@/components/auth/RoleErrorScreen';
import { useClerkGate } from '@/hooks/useClerkGate';
import { AppShell, CommandPalette, KeyboardShortcutsHelp } from '@/components/layout';
import { TopProgressBar } from '@/components/layout/TopProgressBar';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { SimpleRouteTransition } from '@/components/transitions';
import { LazyMotion, domAnimation } from 'framer-motion';
import {
  useKeyboardShortcuts,
  COMMON_SHORTCUTS,
  COMMON_CHORDS,
} from '@/hooks/useKeyboardShortcuts';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { LayoutProvider } from '@/contexts/LayoutContext';
import { TierProvider } from '@/contexts/TierContext';
import { ToastContextProvider } from '@/contexts/ToastContext';
import { EmbeddedOrgProvider } from '@/contexts/EmbeddedOrgContext';
import { EmbeddedRoleProvider } from '@/contexts/EmbeddedRoleProvider';
import { EmbeddedAuthProvider } from '@/contexts/EmbeddedAuthProvider';
import { EmbeddedConsentGate } from '@/features/onboarding/EmbeddedConsentGate';
import { AccessRequestGate } from '@/features/onboarding/AccessRequestGate';
import { EmbeddedOnboardingGate } from '@/features/onboarding/EmbeddedOnboardingGate';
import { EmbeddedComplianceGate } from '@/features/onboarding/EmbeddedComplianceGate';
import { ClerkAuthProvider } from '@/contexts/ClerkAuthProvider';
import { SupportChatIdentityBridge } from '@/components/SupportChatIdentityBridge';
import { OrgLinkNotice } from '@/components/OrgLinkNotice';
import { AssistantDrawerProvider } from '@/contexts/AssistantDrawerContext';
import { WebWhatsNew } from '@/features/whats-new';
import { AssistantHostProvider } from '@/contexts/AssistantHostContext';
import { AssistantDrawer } from '@/components/assistant/AssistantDrawer';
import { useRole, useOrganization } from '@/contexts/RoleContext';
import { useFeatureFlagsSafe } from '@/contexts/FeatureFlagsContext';
import { FeatureGate } from '@/components/ui/FeatureGate';
import { getRouteFeatureFlag } from '@/components/layout/sidebar-config';
import { HelpProvider } from '@/components/help';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { ConsentGate } from '@/features/onboarding/ConsentGate';
import { ComplianceSetupGate } from '@/features/onboarding/ComplianceSetupGate';
import { useConsentStatus } from '@/features/onboarding/useConsentStatus';
import { useCSRFCleanup } from '@/hooks/useCSRFCleanup';
import { useAfterFirstPaint } from '@/hooks/useAfterFirstPaint';
import { useAppReady } from '@/hooks/useAppReady';
import { useRenderedPathname } from '@/hooks/useRenderedPathname';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { RouterContext } from '@/lib/router-context';
import { IS_EMBEDDED_SESSION } from '@/lib/embedded-session';
import { trackFrameViewport } from '@/lib/embedded-frame-viewport';
import {
  isContentSizedEmbeddedPath,
  syncContentSizedDocument,
} from '@/lib/embedded-content-sized';
import { registerApiClient, registerOrgId } from '@/lib/auth-store';
import { RouteRoleGate } from '@/lib/route-role-gate';
import { notificationCountOptions } from '@/lib/query-options';
import { useApiClient } from '@/hooks/useApiClient';
import { useAlertsBadgeCount } from '@/hooks/useAlertsBadgeCount';

function NotFound() {
  const { t } = useTranslation(['auth', 'common']);

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-void">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-fg">404</h1>
        <p className="mt-2 text-lg text-fg-muted">{t('auth.notFound')}</p>
        <a href="/" className="mt-4 inline-block text-primary hover:underline">
          {t('auth.goBackHome')}
        </a>
      </div>
    </div>
  );
}

/**
 * Embedded layout — NO Clerk hooks, NO RoleProvider dependencies.
 * ClerkProvider is not mounted for embedded routes (see main.tsx),
 * so any Clerk hook usage here would crash.
 */
function EmbeddedLayout() {
  const location = useLocation();
  useAppReady();
  // The frame of the CRM tab and of the dashboard is as tall as its content: `html`/`body` stop clipping
  // at the frame height (index.css), the gates below included. Removed on
  // leaving the path.
  useLayoutEffect(() => {
    syncContentSizedDocument(location.pathname);
    return () => {
      syncContentSizedDocument('');
    };
  }, [location.pathname]);
  // Its dialogs open in the part of the frame the portal's window shows (#2678).
  // Keyed on the flag, not the path: moving between content-sized screens keeps
  // the reading instead of dropping it for a moment.
  const contentSized = isContentSizedEmbeddedPath(location.pathname);
  useLayoutEffect(
    () => (contentSized ? trackFrameViewport() : undefined),
    [contentSized]
  );
  return (
    <ThemeProvider defaultTheme="light" storageKey="embedded-aica-theme">
      <EmbeddedOrgProvider>
        <EmbeddedAuthProvider>
          <SupportChatIdentityBridge />
          <EmbeddedRoleProvider>
            <ToastContextProvider>
              <EmbeddedConsentGate>
                <AccessRequestGate>
                  {/* Compliance спрашивается ДО опроса. Порядок был обратным, и
                      между установкой приложения и подтверждением проходило
                      несколько минут: Bitrix24 начинает слать звонки через
                      полминуты после установки, а ingest их отбрасывает, пока
                      compliance не подтверждён. У портала с живым потоком это
                      десятки записей, потерянных безвозвратно. */}
                  <EmbeddedComplianceGate>
                    <EmbeddedOnboardingGate>
                      <LayoutProvider>
                        <HelpProvider>
                          <TooltipProvider delayDuration={300} skipDelayDuration={100}>
                            <ErrorBoundary>
                              <main
                                id="main-content"
                                className={
                                  isContentSizedEmbeddedPath(location.pathname)
                                    ? undefined
                                    : 'h-screen overflow-auto'
                                }
                              >
                                <ErrorBoundary resetKey={location.pathname}>
                                  <Outlet />
                                </ErrorBoundary>
                              </main>
                            </ErrorBoundary>
                          </TooltipProvider>
                        </HelpProvider>
                      </LayoutProvider>
                    </EmbeddedOnboardingGate>
                  </EmbeddedComplianceGate>
                </AccessRequestGate>
              </EmbeddedConsentGate>
            </ToastContextProvider>
          </EmbeddedRoleProvider>
        </EmbeddedAuthProvider>
      </EmbeddedOrgProvider>
    </ThemeProvider>
  );
}

/**
 * Demo layout — no auth, minimal providers.
 */
function DemoLayout() {
  const location = useLocation();
  // See MainAppLayout — key transitions off the RENDERED pathname, not pending.
  const renderedPathname = useRenderedPathname();
  useAppReady();

  return (
    <LazyMotion features={domAnimation}>
      <ThemeProvider>
        <ToastContextProvider>
          <TooltipProvider delayDuration={300} skipDelayDuration={100}>
            <ErrorBoundary>
              <main id="main-content" className="min-h-screen">
                <ErrorBoundary resetKey={location.pathname}>
                  <SimpleRouteTransition routeKey={renderedPathname}>
                    <Outlet />
                  </SimpleRouteTransition>
                </ErrorBoundary>
              </main>
            </ErrorBoundary>
          </TooltipProvider>
        </ToastContextProvider>
      </ThemeProvider>
    </LazyMotion>
  );
}

/**
 * RouteFeatureGate — blocks access to pages whose feature flag is disabled.
 * Uses the sidebar nav config to determine which flag applies to the current route.
 */
function RouteFeatureGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const pathname = location.pathname;

  const matchedFlag = useMemo(() => getRouteFeatureFlag(pathname), [pathname]);

  if (matchedFlag) {
    return <FeatureGate flag={matchedFlag}>{children}</FeatureGate>;
  }

  return <>{children}</>;
}

/**
 * Main app layout — uses Clerk auth, RoleProvider, full navigation.
 * Only rendered when ClerkProvider and RoleProvider are mounted (non-embedded routes).
 */
function MainAppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  // «Whole base» in the drawer opens the page with the filters (#1357). The
  // page closes the drawer on arrival; already on it, there is no arrival,
  // and the answer «already there» lets the drawer close itself.
  const openWholeBase = useCallback(() => {
    if (location.pathname === '/assistant') return true;
    void navigate({ to: '/assistant' });
    return false;
  }, [location.pathname, navigate]);
  // Route-transition key: the pathname the Outlet actually renders. The pending
  // location flips at navigation start (before content swaps) and re-keying the
  // transition from it froze the old route mid-crossfade — see RouteTransition.tsx.
  const renderedPathname = useRenderedPathname();
  // useClerkGate (not Clerk's useAuth): the root layout renders the whole app
  // tree, so a reactive Clerk subscription here re-rendered EVERYTHING on
  // every ~50s token refresh — the root cause of the idle UI-freeze class.
  const { isLoaded: isClerkLoaded, isSignedIn } = useClerkGate();
  const {
    isLoading: isRoleLoading,
    error: roleError,
    orgs,
    selectedOrgId,
    globalRole,
    isOwner,
  } = useRole();
  const { isOrgSwitching, currentOrganization, isErrorRetriable, retryUserData } =
    useOrganization();
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const { isEnabled } = useFeatureFlagsSafe();
  const { t } = useTranslation(['auth', 'common']);
  const { apiClient } = useApiClient();

  // Register apiClient and orgId for use in route loaders (prefetch).
  // Runs on every render to stay in sync with auth state. RBAC route gating
  // is handled in-tree by RouteRoleGate (see src/lib/route-role-gate.tsx),
  // not via the auth-store — a beforeLoad guard deadlocked on cold loads.
  registerApiClient(apiClient);
  registerOrgId(selectedOrgId);

  // Показывали ли уже само приложение. Ставится ЭФФЕКТОМ, а не в рендере:
  // рендер React вправе выбросить (навигации роутера идут транзакцией), и
  // запись в ref пережила бы такой выброшенный рендер — защёлка щёлкнула бы
  // раньше, чем приложение реально показали.
  const hasShownApp = useRef(false);

  // CSRF token cleanup on sign-out
  useCSRFCleanup();

  // Kick off the consent status query at the layout level so its loading
  // state can gate the boot splash. ConsentGate and ComplianceSetupGate
  // reuse the same cached query — endpoint is hit once per org.
  const {
    isLoading: isConsentLoading,
    isSuccess: isConsentReady,
    fetchStatus: consentFetchStatus,
  } = useConsentStatus();

  // Splash stays visible until Clerk, role context, AND consent status are all
  // ready (or until the consent query is idle — e.g. not signed in / no org).
  // Prevents the blank frame between splash fade-out and gate fetches resolving.
  const isConsentSettled =
    isConsentReady || (consentFetchStatus === 'idle' && !isConsentLoading);
  useAppReady(isClerkLoaded && !isRoleLoading && isConsentSettled);

  // Одна стадия и на ветвление рендера ниже, и на защёлку — см. resolveRootStage.
  const stage = resolveRootStage({
    isClerkLoaded,
    isRoleLoading,
    isSignedIn,
    hasRoleError: Boolean(roleError),
    orgCount: orgs.length,
    // sessionStorage читается только там, где ответ вообще нужен: корневой
    // лэйаут перерисовывается на каждой навигации и каждом счётчике, а
    // организаций нет лишь в первые секунды после регистрации.
    hasClaimTicket: orgs.length === 0 && Boolean(storedClaimToken()),
    isConsentSettled,
    hasShownApp: hasShownApp.current,
  });

  useEffect(() => {
    if (stage === 'app') hasShownApp.current = true;
  }, [stage]);

  // Defer non-critical sidebar badges until after first paint to unblock FCP.
  // These counters are cosmetic — zero impact on routing/auth/gate decisions.
  const afterFirstPaint = useAfterFirstPaint();

  // Fetch notification count for badge (uses query options factory for consistency)
  const { data: notificationCountData } = useQuery({
    ...notificationCountOptions(apiClient, selectedOrgId),
    enabled: afterFirstPaint && isSignedIn && !!selectedOrgId && !isRoleLoading,
  });

  // Active alerts + open incidents for the sidebar badge. Both halves live in
  // one hook (useAlertsBadgeCount) because they share one audience rule and one
  // flag — written out twice here, the rule reached the alert count and not the
  // incident list, and agent sessions kept collecting a 403 from
  // GET /api/v1/incidents on every mount.
  const alertsBadgeCount = useAlertsBadgeCount({
    apiClient,
    selectedOrgId,
    ready: afterFirstPaint && isSignedIn && !!selectedOrgId && !isRoleLoading,
    globalRole: globalRole ?? null,
    isOwner,
    alertsEnabled: isEnabled('enableAlerts'),
  });

  // Global keyboard shortcuts (before conditional returns)
  useKeyboardShortcuts({
    shortcuts: [
      {
        ...COMMON_SHORTCUTS.COMMAND_PALETTE,
        handler: () => setIsPaletteOpen(true),
        enabled: isEnabled('enableCommandPalette'),
      },
      {
        ...COMMON_SHORTCUTS.KEYBOARD_HELP,
        handler: () => setIsHelpOpen(true),
        enabled: true,
      },
      {
        ...COMMON_SHORTCUTS.GO_DASHBOARD,
        handler: () => navigate({ to: '/dashboard' }),
        enabled: true,
      },
      {
        ...COMMON_SHORTCUTS.GO_RECORDS,
        handler: () => navigate({ to: '/records' }),
        enabled: true,
      },
      {
        ...COMMON_SHORTCUTS.GO_PROJECTS,
        handler: () => navigate({ to: '/projects' }),
        enabled: true,
      },
      {
        ...COMMON_SHORTCUTS.GO_SETTINGS,
        handler: () => navigate({ to: '/settings' }),
        enabled: true,
      },
    ],
    chords: [
      {
        ...COMMON_CHORDS.GO_DASHBOARD,
        handler: () => navigate({ to: '/dashboard' }),
        enabled: true,
      },
      {
        ...COMMON_CHORDS.GO_RECORDS,
        handler: () => navigate({ to: '/records' }),
        enabled: true,
      },
      {
        ...COMMON_CHORDS.GO_RECORDS_TABLE,
        handler: () => navigate({ to: '/records', search: { view: 'table' } }),
        enabled: true,
      },
      {
        ...COMMON_CHORDS.GO_PROJECTS,
        handler: () => navigate({ to: '/projects' }),
        enabled: true,
      },
      {
        ...COMMON_CHORDS.GO_SETTINGS,
        handler: () => navigate({ to: '/settings' }),
        enabled: true,
      },
      {
        ...COMMON_CHORDS.GO_ALERTS,
        handler: () => navigate({ to: '/alerts' }),
        enabled: true,
      },
      {
        ...COMMON_CHORDS.GO_BILLING,
        handler: () => navigate({ to: '/settings', search: { section: 'billing' } }),
        enabled: true,
      },
    ],
  });

  // Ожидание держит ОДИН экран — тот же кадр, что показывал сплеш и
  // /link-portal. Прежде их было три подряд, на трёх глубинах дерева:
  // AppLayoutSkeleton здесь, второй в ConsentGate, третий в
  // ComplianceSetupGate. Каждый следующий — новый монтаж, а не перерисовка,
  // поэтому шиммер начинался заново и геометрия дёргалась (замер: 129.8–135 с
  // в docs/plans/2026-09-09-first-run-shell-continuity.md).
  // Функция, а не готовый элемент: MainAppLayout — корень всего дерева и
  // перерисовывается на каждой смене маршрута, счётчиков и состояния
  // организации. Собранный заранее элемент означал бы поиск перевода и три
  // аллокации на каждом таком рендере, в том числе на тех, где приложение уже
  // показано и кадр не нужен вовсе.
  const waitingFrame = () => (
    <ThemeProvider>
      <BootFrame headline={t('auth.appLoading.headline')} busy />
    </ThemeProvider>
  );

  // Ветвление — по стадии, и другого источника у него нет: условия описаны один
  // раз в resolveRootStage, там же их читает защёлка выше.
  if (stage === 'waiting') {
    return waitingFrame();
  }

  if (stage === 'signIn') {
    return <SignInScreen />;
  }

  // Транспортный сбой предлагает повтор, отказ сервера — обращение к
  // администратору (см. RoleErrorScreen).
  // `&& roleError` — сужение типа, а не второе условие: стадия уже решена
  // выше, и она получается ровно из этого же значения.
  if (stage === 'roleError' && roleError) {
    return (
      <RoleErrorScreen
        message={roleError}
        retriable={isErrorRetriable}
        onRetry={retryUserData}
      />
    );
  }

  if (stage === 'linkPortal') {
    return <Navigate to="/link-portal" />;
  }

  if (stage === 'onboarding') {
    return <Navigate to="/onboarding" />;
  }

  return (
    <LazyMotion features={domAnimation}>
      <ThemeProvider>
        <ToastContextProvider>
          {/* An email link named an organization the user is not in (#1232). */}
          <OrgLinkNotice />
          <ConsentGate>
            <ComplianceSetupGate>
              <LayoutProvider>
                <ClerkAuthProvider>
                  <SupportChatIdentityBridge />
                  <TierProvider>
                    <HelpProvider
                      userRole={
                        globalRole as 'admin' | 'owner' | 'manager' | 'agent' | undefined
                      }
                    >
                      <TooltipProvider delayDuration={300} skipDelayDuration={100}>
                        {/* Host OUTSIDE the drawer (#922): the record on screen is an
                            input to the topic, so the provider that owns the topic must
                            be able to read it. The old order forced a push call from the
                            drawer and made two places decide the topic. */}
                        <AssistantHostProvider>
                          <AssistantDrawerProvider
                            selectedOrgId={selectedOrgId}
                            enabled={isEnabled('enableAIAssistant')}
                          >
                            <ErrorBoundary>
                              <TopProgressBar />
                              {/* Skip Navigation Link for Accessibility */}
                              <a
                                href="#main-content"
                                className="sr-only sr-only-focusable fixed left-4 top-4 z-toast rounded-button bg-primary px-4 py-2 text-sm font-medium text-bg focus:outline-none focus:ring-2 focus:ring-ring"
                              >
                                {t('auth.skipToMainContent')}
                              </a>

                              <AppShell
                                notificationCount={notificationCountData?.count || 0}
                                alertsBadgeCount={alertsBadgeCount}
                                onCommandPaletteOpen={() => setIsPaletteOpen(true)}
                              >
                                <ErrorBoundary resetKey={location.pathname}>
                                  <RouteRoleGate>
                                    <RouteFeatureGate>
                                      <SimpleRouteTransition routeKey={renderedPathname}>
                                        <Outlet />
                                      </SimpleRouteTransition>
                                    </RouteFeatureGate>
                                  </RouteRoleGate>
                                </ErrorBoundary>
                              </AppShell>

                              {/* Command Palette (gated by feature flag) */}
                              {isEnabled('enableCommandPalette') && (
                                <CommandPalette
                                  isOpen={isPaletteOpen}
                                  onClose={() => setIsPaletteOpen(false)}
                                />
                              )}

                              {/* AI assistant panel (#765) — renders nothing while the provider above is off (tier below Pro, or an org override) */}
                              <AssistantDrawer
                                apiClient={apiClient}
                                selectedOrgId={selectedOrgId}
                                onOpenWholeBase={openWholeBase}
                              />

                              {/* Keyboard Shortcuts Help */}
                              <KeyboardShortcutsHelp
                                isOpen={isHelpOpen}
                                onClose={() => setIsHelpOpen(false)}
                              />

                              {/* Поп-ап «что нового» (#946): один раз на релиз,
                                  тому, кто вошёл после выката. Внутри TierProvider —
                                  пункт про недоступную на тарифе возможность
                                  показывается с пометкой, а не прячется. */}
                              <WebWhatsNew />

                              {/* Organization Switching Overlay */}
                              <LoadingOverlay
                                visible={isOrgSwitching}
                                fullscreen
                                message={`${t('common:common.switchingTo')} ${currentOrganization?.name || ''}...`}
                                blurIntensity="lg"
                              />
                            </ErrorBoundary>
                          </AssistantDrawerProvider>
                        </AssistantHostProvider>
                      </TooltipProvider>
                    </HelpProvider>
                  </TierProvider>
                </ClerkAuthProvider>
              </LayoutProvider>
            </ComplianceSetupGate>
          </ConsentGate>
        </ToastContextProvider>
      </ThemeProvider>
    </LazyMotion>
  );
}

/**
 * Root layout — routes to the correct layout based on pathname.
 * Embedded and demo routes are handled by dedicated components that
 * do NOT call any Clerk hooks (ClerkProvider is not mounted for them).
 */
/**
 * Public layout — minimal providers, no Clerk auth required.
 * Used for invitation acceptance and other public pages.
 */
function PublicLayout({ deferAppReady = false }: { deferAppReady?: boolean }) {
  const location = useLocation();
  // `/link-portal` объявляет готовность сам (см. LinkPortalScreen): здесь она
  // означала бы «компонент смонтировался», и сплеш уходил бы за четыре секунды
  // до того, как под ним появится хоть что-то, кроме пустой карточки Clerk.
  useAppReady(!deferAppReady);
  return (
    <ThemeProvider>
      <ToastContextProvider>
        {/* A user with no organizations is sent to /onboarding or /link-portal,
            both rendered here; the link they opened still owes them its notice (#1232). */}
        <OrgLinkNotice />
        <TooltipProvider delayDuration={300} skipDelayDuration={100}>
          <ErrorBoundary>
            <main id="main-content" className="min-h-screen bg-bg-void">
              <ErrorBoundary resetKey={location.pathname}>
                <Outlet />
              </ErrorBoundary>
            </main>
          </ErrorBoundary>
        </TooltipProvider>
      </ToastContextProvider>
    </ThemeProvider>
  );
}

function RootLayout() {
  const pathname = useRenderedPathname();

  if (pathname.startsWith('/demos')) {
    return <DemoLayout />;
  }

  if (pathname.startsWith('/embedded')) {
    return <EmbeddedLayout />;
  }

  // Safety net: an embedded session has no ClerkProvider (see main.tsx). A stray
  // client-side navigation out of /embedded (e.g. a dashboard drill-down) would
  // otherwise fall through to the Clerk-dependent MainAppLayout and crash with
  // "useClerk outside <ClerkProvider />". Bounce back into the embedded shell.
  if (IS_EMBEDDED_SESSION) {
    return <Navigate to="/embedded/dashboard" replace />;
  }

  if (pathname.startsWith('/invitations')) {
    return <PublicLayout />;
  }

  // Public auth routes (sign-in / sign-up) render via the minimal PublicLayout,
  // not the gated MainAppLayout — so a signed-out visitor gets the branded form
  // (not the inline sign-in gate) and the authed app-shell hooks never mount on
  // the highest-traffic logged-out pages. Trailing slash tolerated.
  const authPath = pathname.replace(/\/+$/, '');
  if (authPath === '/sign-in' || authPath === '/sign-up') {
    return <PublicLayout />;
  }

  // Мост из CRM-портала: сюда приходят ещё не залогиненными, и экран сам
  // показывает форму входа Clerk — гейт выше пропускает его как публичный.
  if (authPath === '/link-portal') {
    return <PublicLayout deferAppReady />;
  }

  if (pathname === '/terms' || pathname === '/privacy' || pathname === '/onboarding') {
    return <PublicLayout />;
  }

  return <MainAppLayout />;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
});
